<template>
  <div :class="`right-slider-modal ${show ? 'expand' : ''}`">
    <div class="main">
      <img
        class="cross_icon"
        src="/img/cross-x_icon.svg"
        alt="cross"
        @click="closeModal"
      />

      <div v-if="show" class="tw-h-full tw-flex tw-flex-col">
        <component
          :is="modalComponent[modal.component]"
          :show="show"
          v-bind="modal.properties"
          v-on="{ ...modal.events }"
          @close="closeModal"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeMount, onBeforeUnmount, ref, watch } from "vue";
import EventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import { RIGHT_SLIDE_MODAL_NAME } from "@/utils/constants";
import AssessmentAdvanced from "@/components/assessment/assessmentAdvanced.vue";
import vendorAssessmentExternalAdvanceModal from "@/components/vendor/vendorAssessmentExternalAdvanceModal.vue";
import { IModalMessage } from "@/interfaces/common/common";
import store from "@/store";

const emit = defineEmits(["show", "close"]);
const props = defineProps({
  isShow: { type: Boolean, required: true, default: false },
  isShowOverlay: { type: Boolean, required: false, default: true },
});

// Import component modal here.
const modalComponent = {
  [RIGHT_SLIDE_MODAL_NAME.ASSESSMENT_ADVANCED]: AssessmentAdvanced,
  [RIGHT_SLIDE_MODAL_NAME.ASSESSMENT_REQUESTED_ADVANCED]:
    vendorAssessmentExternalAdvanceModal,
};

const show = ref(false);
const modal: IModalMessage = {
  component: "",
  events: {},
};

onBeforeMount(() => {
  listeningEmitEvent();
});

onBeforeUnmount(() => {
  removeEmitEventListener();
});

const listeningEmitEvent = () => {
  EventEmitter.on(EmitterEvents.OPEN_RIGHT_SLIDE_MODAL, showModal);
  document.addEventListener(
    "keydown",
    (e) => e.key === "Escape" && closeModal(),
  );
};

const removeEmitEventListener = () => {
  EventEmitter.off(EmitterEvents.OPEN_RIGHT_SLIDE_MODAL);
  document.removeEventListener("keydown", () => {});
};

const showModal = async (value) => {
  // Show modal only for Assessment Advanced Search box.
  if (
    value.component === RIGHT_SLIDE_MODAL_NAME.ASSESSMENT_ADVANCED &&
    value.properties?.showOverlay === false
  ) {
    if (value.properties?.questionData && value.properties?.checklistItemId) {
      store.commit("Assessment/SET_CHECKLIST_CURRENT_CHECKLIST_ADVANCED", {
        questionData: value.properties.questionData,
        checklistId: value.properties.checklistId,
        checklistItemId: value.properties.checklistItemId,
        answerOptionsDropDown: value.properties.answerOptionsDropDown,
        answerOption: value.properties.answerOption,
        isRecommend: value.properties.isRecommend,
        isGlobalSearch: value.properties.isGlobalSearch || false,
        show: value.properties.show,
        passkey: value.properties.passkey,
      });
    }

    /**
     * If the modal is already open and the component is the same
     * we don't need to emit show again.
     */
    if (modal.component === value.component) return;
  }

  modal.component = value.component;
  modal.events = value.events;
  modal.properties = value.properties;

  if (modal.component in modalComponent) {
    const showOverlay =
      modal.properties?.showOverlay !== undefined
        ? modal.properties?.showOverlay
        : true;

    await new Promise((resolve) => setTimeout(resolve, 10));
    show.value = true;
    emit("show", showOverlay);
  }
};

const closeModal = () => {
  show.value = false;
  emit("close");

  // Reset modal component and properties when closing.
  modal.component = "";
  modal.events = {};
  modal.properties = {};
  store.commit("Assessment/SET_CHECKLIST_CURRENT_CHECKLIST_ADVANCED", {});
};

watch(
  () => props.isShow,
  () => props.isShow === false && closeModal(),
);
</script>

<style lang="scss" scoped>
.right-slider-modal {
  max-width: 640px;
  width: 0px;
  height: 100%;
  position: fixed;
  top: 0;
  right: 0;
  z-index: 1071;
  background: #ffffff;
  -webkit-transition: right 0.2s ease;
  -moz-transition: right 0.2s ease;
  -o-transition: right 0.2s ease;
  -ms-transition: right 0.2s ease;
  transition: right 0.2s ease;
  overflow-x: hidden;

  &.expand {
    width: 100%;
  }

  .main {
    display: flex;
    flex-direction: column;
    position: relative;
    width: 100%;
    height: 100%;
  }

  .cross_icon {
    width: 16.09px;
    height: 16.09px;
    margin-top: 52px;
    margin-right: 40px;
    margin-left: auto;
    cursor: pointer;
  }
}
</style>
